(() => {
// Family tags use a nine-character SKU base, optionally followed by a modifier.
// Storefront search requires complete tags; tag-prefix wildcards return no items.
const skuModifiers = ['', 'L', 'N', 'C', 'R', 'X'];
const searchBatchSize = 100;

class NfdProductFamilySelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') {
      return;
    }

    this.dataset.initialized = 'true';
    this.loadFamily().catch(() => {
      // Enhancement only: leave the selector hidden and preserve the PDP.
    });
  }

  get selectorConfig() {
    return null;
  }

  getSearchQueries(familyValue) {
    const isColorSelector = this.selectorConfig.filterFitCode;
    const fitCode = String(this.dataset.fitCode || '').trim();

    const familyPattern = isColorSelector
      ? /^(?:\d{6}|[A-Z]{3}\d{3})$/
      : /^(?:\d{7}|[A-Z]{3}\d{4})$/;

    if (!familyPattern.test(familyValue) ||
        (isColorSelector && !/^\d{2}$/.test(fitCode))) {
      return [];
    }

    const tags = [];
    const codeCount = isColorSelector ? 10 : 100;

    for (let code = 0; code < codeCount; code += 1) {
      const baseSku = isColorSelector
        ? `${familyValue}${code}${fitCode}`
        : `${familyValue}${String(code).padStart(2, '0')}`;

      for (const modifier of skuModifiers) {
        tags.push(`tag:__${baseSku}${modifier}`);
      }
    }

    const queries = [];

    for (let index = 0; index < tags.length; index += searchBatchSize) {
      queries.push(tags.slice(index, index + searchBatchSize).join(' OR '));
    }

    return queries;
  }

  async searchFamily(searchUrl, query, config) {
    const endpoint = new URL(searchUrl, window.location.origin);
    endpoint.searchParams.set('q', query);
    endpoint.searchParams.set('type', 'product');
    endpoint.searchParams.set('view', config.view);
    endpoint.searchParams.set('options[unavailable_products]', 'show');

    const items = [];
    const visitedPages = new Set();
    let pageUrl = endpoint.toString();

    while (pageUrl && !visitedPages.has(pageUrl)) {
      visitedPages.add(pageUrl);

      const response = await fetch(pageUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        throw new Error('Product family search failed.');
      }

      const responseTemplate = document.createElement('template');
      responseTemplate.innerHTML = await response.text();
      const results = responseTemplate.content.querySelector(
        config.resultsSelector
      );

      if (!results) {
        break;
      }

      items.push(...results.querySelectorAll(config.itemSelector));
      const nextPage = results.dataset.productFamilyNextUrl;
      pageUrl = nextPage
        ? new URL(nextPage, window.location.origin).toString()
        : null;
    }

    return items;
  }

  async loadFamily() {
    const config = this.selectorConfig;
    const familyValue = String(
      this.dataset[config.familyDataset] || ''
    ).trim();
    const currentHandle = String(this.dataset.currentHandle || '').trim();
    const searchUrl = String(this.dataset.searchUrl || '').trim();
    const manualTemplate = config.manualTemplateSelector
      ? this.querySelector(config.manualTemplateSelector)
      : null;
    let items = manualTemplate
      ? Array.from(manualTemplate.content.querySelectorAll(config.itemSelector))
      : [];
    let results = null;

    if (!currentHandle) {
      return;
    }

    const canSearch =
      familyValue.length === config.familyLength && Boolean(searchUrl);

    if (canSearch) {
      const searches = await Promise.allSettled(
        this.getSearchQueries(familyValue).map((query) => {
          return this.searchFamily(searchUrl, query, config);
        })
      );

      for (const search of searches) {
        if (search.status === 'fulfilled') {
          items.push(...search.value);
        }
      }
      // Manual additions and successful batches survive a failed search.
    }

    if (items.length === 0) {
      return;
    }

    if (config.filterFitCode) {
      const fitCode = String(this.dataset.fitCode || '').trim();

      if (fitCode.length !== 2) {
        return;
      }

      items = items.filter((item) => item.dataset.fitCode === fitCode);
    }

    if (config.filterFabricKey) {
      const fabricKey = String(this.dataset.fabricKey || '').trim();

      if (!fabricKey) {
        return;
      }

      items = items.filter((item) => item.dataset.fabricKey === fabricKey);
    }

    const seenHandles = new Set();

    items = items.filter((item) => {
      const productHandle = String(item.dataset.productHandle || '').trim();

      if (!productHandle || seenHandles.has(productHandle)) {
        return false;
      }

      seenHandles.add(productHandle);
      return true;
    });

    const currentItem = items.find((item) => {
      return item.dataset.productHandle === currentHandle;
    });
    const siblingItems = items.filter((item) => item !== currentItem);

    if (!currentItem || siblingItems.length === 0) {
      return;
    }

    const getItemPriority = (item) => {
      const isUnavailable = item.dataset.productAvailable === 'false';
      const isJacket = String(
        item.dataset.productType || ''
      ).includes('jacket');

      if (isUnavailable) {
        return 2;
      }

      return isJacket ? 1 : 0;
    };

    items.sort((firstItem, secondItem) => {
      const firstPriority = getItemPriority(firstItem);
      const secondPriority = getItemPriority(secondItem);

      if (firstPriority !== secondPriority) {
        return firstPriority - secondPriority;
      }

      if (firstItem === currentItem) {
        return -1;
      }

      if (secondItem === currentItem) {
        return 1;
      }

      return String(firstItem.dataset[config.labelDataset] || '').localeCompare(
        String(secondItem.dataset[config.labelDataset] || ''),
        document.documentElement.lang || undefined,
        { sensitivity: 'base' }
      );
    });

    const currentCard = currentItem.querySelector('.nfd-fit-family__card');

    const hasUniformComingSoonStatus = items.every((item) => {
      return Boolean(
        item.querySelector('.nfd-fit-family__status--coming-soon')
      );
    });

    this.classList.toggle(
      'has-uniform-coming-soon',
      hasUniformComingSoonStatus
    );

    if (currentCard) {
      currentCard.classList.add('is-current');
      currentCard.setAttribute('aria-current', 'page');
    }

    if (!results) {
      results = document.createElement('ul');
      results.className = 'nfd-fit-family__list';
      results.setAttribute(config.resultsAttribute, '');
      results.setAttribute('role', 'list');
    }

    results.replaceChildren(...items);

    const heading = this.querySelector('.nfd-fit-family__heading');
    const content = this.querySelector(config.contentSelector);

    if (!content) {
      return;
    }

    if (heading?.id) {
      results.setAttribute('aria-labelledby', heading.id);
    }

    content.replaceChildren(results);
    this.hidden = false;

    const familyWrapper = this.closest('[data-product-family-options]');

    if (familyWrapper) {
      familyWrapper.hidden = false;
    }
  }
}

class NfdFabricFitSelector extends NfdProductFamilySelector {
  get selectorConfig() {
    return {
      familyDataset: 'familyPrefix',
      familyLength: 7,
      view: 'fit-options',
      resultsSelector: '[data-fit-family-results]',
      resultsAttribute: 'data-fit-family-results',
      itemSelector: '[data-fit-family-item]',
      labelDataset: 'fitLabel',
      contentSelector: '[data-fit-family-content]',
      manualTemplateSelector: '[data-manual-fit-items]',
      filterFitCode: false,
      filterFabricKey: false
    };
  }
}

class NfdFabricColorSelector extends NfdProductFamilySelector {
  get selectorConfig() {
    return {
      familyDataset: 'colorFamilyRoot',
      familyLength: 6,
      view: 'color-options',
      resultsSelector: '[data-color-family-results]',
      resultsAttribute: 'data-color-family-results',
      itemSelector: '[data-color-family-item]',
      labelDataset: 'colorLabel',
      contentSelector: '[data-color-family-content]',
      manualTemplateSelector: null,
      filterFitCode: true,
      filterFabricKey: true
    };
  }
}

if (!customElements.get('fabric-fit-selector')) {
  customElements.define('fabric-fit-selector', NfdFabricFitSelector);
}

if (!customElements.get('fabric-color-selector')) {
  customElements.define('fabric-color-selector', NfdFabricColorSelector);
}
})();
